import React from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { ClipboardList, Users, UserCheck, BarChart3, Briefcase, FileStack, Building2, CalendarCheck, ShieldCheck, Contact } from 'lucide-react';

const boxBaseStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    minHeight: '160px',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
    boxSizing: 'border-box'
};

const boxTitleStyle = {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    margin: 0
};

const boxSubtitleStyle = {
    fontSize: '12px',
    color: '#94a3b8',
    margin: 0,
    textAlign: 'center'
};

const DashboardBox = ({ icon, title, subtitle, onClick, active }) => (
    <div
        onClick={onClick}
        style={{
            ...boxBaseStyle,
            borderColor: active ? '#2563eb' : '#e2e8f0',
            boxShadow: active ? '0 4px 12px rgba(37,99,235,0.15)' : boxBaseStyle.boxShadow
        }}
    >
        <div style={{ color: active ? '#2563eb' : '#64748b' }}>{icon}</div>
        <h3 style={boxTitleStyle}>{title}</h3>
        {subtitle && <p style={boxSubtitleStyle}>{subtitle}</p>}
    </div>
);

// showJobGathering / showCreatedTasks: Team-Lead-only boxes.
// onTasksClick: if provided, Tasks box navigates to a route instead of toggling
// activeSection inline (used by both roles now that Tasks has its own page).
const DashboardHome = ({
    user, activeSection, onSectionSelect,
    showJobGathering = false, onJobGatheringClick,
    showCreatedTasks = false, onCreatedTasksClick,
    showCompanyDetails = false, onCompanyDetailsClick,
    showCandidateDetails = false, onCandidateDetailsClick,
    // Named Freelancer Details: all three roles.
    showFreelancers = false, onFreelancersClick,
    // Attendance: Admin always; Team Lead only when they're the one
    // designated account (can_manage_attendance) — never a Recruiter.
    showAttendance = false, onAttendanceClick,
    // Account Control: Admin only.
    showAccountControl = false,
    onAccountControlClick,
    onTasksClick, onAnalyticsClick, onInterviewsClick
}) => {
    const { isMobile, isTablet } = useResponsive();
    // const firstName = (user?.name || '').split(' ')[0];

    const handleTasksClick = () => {
        if (onTasksClick) {
            onTasksClick();
        } else {
            onSectionSelect(activeSection === 'tasks' ? null : 'tasks');
        }
    };

    const handleInterviewsClick = () => {
        if (onInterviewsClick) {
            onInterviewsClick();
        } else {
            onSectionSelect(activeSection === 'interviews' ? null : 'interviews');
        }
    };

    // Top row column count grows with however many role-specific boxes are shown.
    const topRowCount = 2 + (showJobGathering ? 1 : 0) + (showCreatedTasks ? 1 : 0) + (showCompanyDetails ? 1 : 0) + (showCandidateDetails ? 1 : 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
            {/* Greeting */}
            <div style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                padding: '20px 24px',
                textAlign: 'center'
            }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
                    Hi {user?.name}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>
                    Make each day your masterpiece. Today is a gift.
                </p>
            </div>

            {/* Top row: Job Gathering + Created Tasks (Team Lead only) + Company Details (Admin only) + Tasks + Interviews */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                    ? '1fr'
                    : isTablet
                        ? `repeat(${Math.min(topRowCount, 2)}, minmax(0, 1fr))`
                        : `repeat(${topRowCount}, minmax(0, 1fr))`,
                gap: '20px'
            }}>
                {showJobGathering && (
                    <DashboardBox
                        icon={<Briefcase size={28} />}
                        title="Job Gathering"
                        subtitle="Job Requirements Tracker"
                        active={false}
                        onClick={onJobGatheringClick}
                    />
                )}

                {showCreatedTasks && (
                    <DashboardBox
                        icon={<FileStack size={28} />}
                        title="Created Tasks"
                        subtitle="Tasks you've assigned"
                        active={false}
                        onClick={onCreatedTasksClick}
                    />
                )}

                <DashboardBox
                    icon={<ClipboardList size={28} />}
                    title="Tasks"
                    subtitle={onTasksClick ? 'View task progress' : (activeSection === 'tasks' ? 'Click to hide' : 'Click to view')}
                    active={activeSection === 'tasks'}
                    onClick={handleTasksClick}
                />

                {showCandidateDetails && (
                    <DashboardBox
                        icon={<Users size={28} />}
                        title="Candidate Details"
                        subtitle="Candidates & KPI Details"
                        active={false}
                        onClick={onCandidateDetailsClick}
                    />
                )}

                <DashboardBox
                    icon={<UserCheck size={28} />}
                    title="Interviews"
                    subtitle={onInterviewsClick ? 'Interviews & Onboarding' : (activeSection === 'interviews' ? 'Click to hide' : 'Click to view')}
                    active={activeSection === 'interviews'}
                    onClick={handleInterviewsClick}
                />

                {showCompanyDetails && (
                    <DashboardBox
                        icon={<Building2 size={28} />}
                        title="Company Details"
                        subtitle="Manage the Company Master"
                        active={false}
                        onClick={onCompanyDetailsClick}
                    />
                )}
            </div>

            {/* Bottom: Attendance (Admin + the one designated Team Lead only), side by side with Analytics & Reports */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (((showAttendance ? 1 : 0) + (showAccountControl ? 1 : 0) + (showFreelancers ? 1 : 0)) > 0 ? `repeat(${1 + (showAttendance ? 1 : 0) + (showAccountControl ? 1 : 0) + (showFreelancers ? 1 : 0)}, 1fr)` : '1fr'), gap: '20px' }}>
                {showFreelancers && (
                    <DashboardBox
                        icon={<Contact size={28} />}
                        title="Freelancer Details"
                        subtitle="Freelancer directory"
                        active={false}
                        onClick={onFreelancersClick}
                    />
                )}
                {showAttendance && (
                    <DashboardBox
                        icon={<CalendarCheck size={28} />}
                        title="Attendance"
                        subtitle="Mark & review recruiter attendance"
                        active={false}
                        onClick={onAttendanceClick}
                    />
                )}
                {showAccountControl && (
                    <DashboardBox
                        icon={<ShieldCheck size={28} />}
                        title="Account Control"
                        subtitle="Users, roles, passwords & access"
                        active={false}
                        onClick={onAccountControlClick}
                    />
                )}
                <DashboardBox
                    icon={<BarChart3 size={28} />}
                    title="Analytics & Reports"
                    subtitle={onAnalyticsClick ? 'View your task & job insights' : 'Coming soon'}
                    active={activeSection === 'analytics'}
                    onClick={() => {
                        if (onAnalyticsClick) {
                            onAnalyticsClick();
                        } else {
                            onSectionSelect(activeSection === 'analytics' ? null : 'analytics');
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default DashboardHome;